'use client';

import { useState } from 'react';
import {
  Gift,
  Leaf,
  MoonStar,
  PartyPopper,
  type LucideIcon,
} from 'lucide-react';

import {
  GIFT_CARD_ASPECT,
  resolveGiftCardStyle,
  scrimBackground,
  type GiftCardLook,
  type GiftCardThemeKey,
} from '@/lib/gift-card-design';
import { mergeClassNames } from '@/lib/utils';

const THEME_ICONS: Record<GiftCardThemeKey, LucideIcon> = {
  classic: Gift,
  midnight: MoonStar,
  botanical: Leaf,
  celebration: PartyPopper,
  minimal: Gift,
  silk: Gift,
  black_gold: Gift,
  golden_hour: Gift,
  terracotta: Gift,
  green_rings: Gift,
  green_cairn: Gift,
  mint_scatter: Gift,
  green_tube: Gift,
};

// Classic paints the studio's live button color; only a sale freezes it. The
// fallback is the appearance default, the hex the server freezes when a
// studio's own is missing.
const BRAND_COLOR = 'var(--brand-button, #F97066)';

type GiftCardProps = {
  studioName: string;
  look: GiftCardLook;
  // The value: "5 classes" or the amount.
  headline: string;
  // Sold cards only.
  code?: string;
  recipientName?: string;
  // The color frozen at sale, which a later rebrand must not repaint.
  brandColor?: string | null;
  // Listing tiles: studio, icon and headline only.
  compact?: boolean;
  // A listing row rounds the card at 12px like the row beside it rather than
  // by its own width.
  flat?: boolean;
  // A checkout thumbnail is too small to read: the background alone, rounded
  // at 8px, with the text beside it saying what the card would.
  bare?: boolean;
  className?: string;
};

// Bank-card proportions with a fixed anatomy on every look: studio name top
// left, theme icon top right, the value large, then the code and who it is
// for. Every length is in container-query units, so the same markup is the
// hero and the tile; the clamps keep the small lines legible on a tile. No
// drop shadow anywhere: the card sits flat on the page.
export const GiftCard = ({
  studioName,
  look,
  headline,
  code,
  recipientName,
  brandColor,
  compact = false,
  flat = false,
  bare = false,
  className,
}: GiftCardProps) => {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const style = resolveGiftCardStyle(look, brandColor ?? BRAND_COLOR);
  const image = style.image && style.image !== failedImage ? style.image : null;
  const Icon = look.type === 'theme' ? THEME_ICONS[look.theme_key] : Gift;

  return (
    <div
      className={mergeClassNames(
        'w-full [container-type:inline-size]',
        className
      )}
    >
      <div
        className={mergeClassNames(
          'relative w-full overflow-hidden',
          flat ? 'rounded-xl' : bare ? 'rounded-lg' : 'rounded-[4.2cqw]'
        )}
        style={{
          aspectRatio: GIFT_CARD_ASPECT,
          backgroundColor: style.bg,
          color: style.text,
          boxShadow: style.border
            ? `inset 0 0 0 1px ${style.border}`
            : undefined,
        }}
      >
        {image && (
          <img
            src={image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setFailedImage(image)}
          />
        )}
        {style.pattern && (
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage: style.pattern.image,
              backgroundSize: `${style.pattern.size / 3}cqw`,
            }}
          />
        )}
        {style.scrim && (
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ backgroundImage: scrimBackground(style.scrim) }}
          />
        )}

        {!bare && (
          <div className="relative flex h-full flex-col justify-between p-[5.5cqw]">
            <div className="flex items-start justify-between gap-[3cqw]">
              <span className="min-w-0 truncate text-[clamp(11px,4.4cqw,18px)] font-semibold leading-[1.2] tracking-[-0.01em]">
                {studioName}
              </span>
              <Icon
                aria-hidden
                className="size-[clamp(16px,6.5cqw,26px)] shrink-0"
                strokeWidth={1.75}
              />
            </div>

            {/* The footer sits close under the value; a sold card's code keeps
              its room through its own margin. */}
            <div className="flex flex-col gap-[0.6cqw]">
              <span
                className={mergeClassNames(
                  'font-display font-bold leading-[1.05] tracking-[-0.02em]',
                  compact ? 'text-[13cqw]' : 'text-[12cqw]'
                )}
              >
                {headline}
              </span>
              {!compact && (
                <>
                  {code && (
                    <span className="my-[1cqw] font-mono text-[4.6cqw] leading-none tracking-[0.1em]">
                      {code}
                    </span>
                  )}
                  <span
                    className="min-w-0 truncate text-[clamp(11px,4cqw,17px)] leading-[1.2]"
                    style={{ color: style.text_muted }}
                  >
                    {recipientName ? `For ${recipientName}` : 'Gift card'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
